import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { ArrowRight, Check, HelpCircle } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { DamageZone } from "../types"
import { FlowCta, FlowHeading, PhasePane, StickyActions } from "../flow-chrome"
import { AREA_PARTS, AREAS, areasWith, singlePart, type CarArea } from "./areas"
import { CAPTURE_COPY, ZONE_NAME } from "./capture-copy"
import { ZONE_ORDER } from "./shot-plan"

// "Where's the damage?" in two taps: the side of the car (a line drawing of
// that view), then which part of it. Every part is one of the 14 spots the
// walk is built from. "Not sure" is a full answer, not a failure.

const C = CAPTURE_COPY.zones

export function AreaPicker({
  initialZones,
  initialUnsure,
  onDone,
}: {
  initialZones: readonly DamageZone[]
  initialUnsure: boolean
  onDone: (zones: DamageZone[], unsure: boolean) => void
}) {
  const [zones, setZones] = useState<DamageZone[]>(() => [...initialZones])
  // Areas open on screen: the ones with a picked part, plus any just tapped.
  const [open, setOpen] = useState<CarArea[]>(() => areasWith(initialZones))
  const [unsure, setUnsure] = useState(initialUnsure)
  const [nudge, setNudge] = useState<string | null>(null)
  const reduce = useReducedMotion()

  const toggleArea = (area: CarArea) => {
    setNudge(null)
    setUnsure(false)
    if (open.includes(area)) {
      setOpen((o) => o.filter((a) => a !== area))
      const parts = AREA_PARTS[area].map((p) => p.zone)
      setZones((z) => z.filter((x) => !parts.includes(x)))
      return
    }
    setOpen((o) => AREAS.filter((a) => a === area || o.includes(a)))
    const only = singlePart(area)
    if (only) setZones((z) => (z.includes(only) ? z : [...z, only]))
  }

  const togglePart = (zone: DamageZone) => {
    setNudge(null)
    setZones((z) => (z.includes(zone) ? z.filter((x) => x !== zone) : [...z, zone]))
  }

  const chooseUnsure = () => {
    setNudge(null)
    setZones([])
    setOpen([])
    setUnsure((u) => !u)
  }

  const submit = () => {
    if (unsure && zones.length === 0) {
      onDone([], true)
      return
    }
    if (open.length === 0) {
      setNudge(C.nudgeNothing)
      return
    }
    const missing = open.find((area) => !AREA_PARTS[area].some((p) => zones.includes(p.zone)))
    if (missing) {
      setNudge(C.nudgePart(C.area[missing]))
      return
    }
    onDone(
      ZONE_ORDER.filter((z) => zones.includes(z)),
      false,
    )
  }

  const withParts = open.filter((area) => AREA_PARTS[area].length > 1)

  return (
    <PhasePane className="pt-2">
      <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">{C.title}</FlowHeading>
      <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{C.sub}</p>

      <div className="mt-5 grid grid-cols-2 gap-2.5 md:grid-cols-3">
        {AREAS.map((area) => {
          const picked = open.includes(area)
          return (
            <button
              key={area}
              type="button"
              aria-pressed={picked}
              aria-label={C.areaName[area]}
              onClick={() => toggleArea(area)}
              className={cx(
                "group relative flex flex-col overflow-hidden rounded-2xl bg-white text-left transition-[box-shadow,background-color] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
                picked ? "bg-signal/[0.07] ring-2 ring-signal" : "ring-1 ring-ink/10 hover:ring-ink/25",
              )}
            >
              <img
                src={`/intake-examples/view-${area}.png`}
                alt=""
                draggable={false}
                className="aspect-[3/2] w-full object-contain px-2 pt-2"
              />
              <span className="flex min-h-11 items-center px-3 pb-2.5 text-[15px] font-semibold text-ink">
                {C.area[area]}
              </span>
              <AnimatePresence>
                {picked && (
                  <motion.span
                    initial={reduce ? false : { scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0, transition: { duration: 0.1 } }}
                    transition={{ type: "spring", stiffness: 520, damping: 22 }}
                    className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-signal text-white"
                    aria-hidden
                  >
                    <Check className="size-4" strokeWidth={3} />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          )
        })}
        <button
          type="button"
          aria-pressed={unsure}
          onClick={chooseUnsure}
          className={cx(
            "flex flex-col justify-center gap-1 rounded-2xl px-4 py-4 text-left transition-[box-shadow,background-color] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
            unsure ? "bg-ink text-white" : "bg-white text-ink ring-1 ring-ink/10 hover:ring-ink/25",
          )}
        >
          {unsure ? <Check className="size-6" aria-hidden /> : <HelpCircle className="size-6 text-zinc-500" aria-hidden />}
          <span className="mt-1 text-[15px] font-semibold">{C.unsure}</span>
          <span className={cx("text-[13px] leading-snug", unsure ? "text-white/80" : "text-zinc-600")}>
            {C.unsureSub}
          </span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {withParts.map((area) => (
          <motion.section
            key={area}
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className="mt-5"
            aria-label={C.partsTitle[area]}
          >
            <h2 className="text-[15px] font-semibold text-ink">{C.partsTitle[area]}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {AREA_PARTS[area].map((part) => {
                const on = zones.includes(part.zone)
                return (
                  <button
                    key={part.zone}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-label={ZONE_NAME[part.zone]}
                    onClick={() => togglePart(part.zone)}
                    className={cx(
                      "inline-flex h-12 items-center gap-2 rounded-full px-5 text-[15px] font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
                      on ? "bg-ink text-white" : "bg-white text-ink ring-1 ring-ink/15 hover:ring-ink/30",
                    )}
                  >
                    {on && <Check className="size-4" strokeWidth={3} aria-hidden />}
                    {part.label}
                  </button>
                )
              })}
            </div>
          </motion.section>
        ))}
      </AnimatePresence>

      {nudge && (
        <p role="alert" className="mt-4 text-[14px] font-medium text-amber-800">
          {nudge}
        </p>
      )}

      <StickyActions>
        <FlowCta onClick={submit}>
          {C.next}
          <ArrowRight className="size-4" aria-hidden />
        </FlowCta>
      </StickyActions>
    </PhasePane>
  )
}
